import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { pageContentsAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Save, Image as ImageIcon, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

const AdminPages = () => {
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [openSection, setOpenSection] = useState(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const keys = ['hero', 'featured_products', 'differentials', 'cta', 'about_company', 'footer', 'how_it_works'];
      const data = {};
      for (const key of keys) {
        try {
          data[key] = await pageContentsAPI.get(key);
        } catch {
          data[key] = { pageKey: key, title: '', subtitle: '', content: '', metadata: {} };
        }
      }
      setSections(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const updateField = (key, field, value) => {
    setSections(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  const updateMeta = (key, field, value) => {
    setSections(prev => ({
      ...prev,
      [key]: { ...prev[key], metadata: { ...(prev[key]?.metadata || {}), [field]: value } }
    }));
  };

  const saveSection = async (key) => {
    setSaving(key);
    try {
      const s = sections[key];
      await pageContentsAPI.update(key, { title: s.title, subtitle: s.subtitle, content: s.content, metadata: s.metadata });
      alert('Seção salva com sucesso!');
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(null); }
  };

  const toggle = (key) => setOpenSection(openSection === key ? null : key);

  if (loading) return <AdminLayout><div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#055509]" /></div></AdminLayout>;

  const ImageField = ({ label, value, onChange }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex gap-2 items-start">
        <Input value={value || ''} onChange={e => onChange(e.target.value)} placeholder="URL da imagem" className="flex-1" />
        {value && <img src={value} alt="preview" className="w-16 h-16 object-cover rounded border" onError={e => { e.target.style.display = 'none'; }} />}
      </div>
    </div>
  );

  const SectionWrapper = ({ sectionKey, title, children }) => (
    <Card>
      <CardHeader className="cursor-pointer" onClick={() => toggle(sectionKey)}>
        <div className="flex justify-between items-center">
          <CardTitle className="text-base">{title}</CardTitle>
          <div className="flex items-center gap-2">
            {openSection === sectionKey ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </div>
        </div>
      </CardHeader>
      {openSection === sectionKey && (
        <CardContent className="border-t space-y-4 pt-4">
          {children}
          <div className="flex justify-end pt-2">
            <Button onClick={() => saveSection(sectionKey)} className="bg-[#055509] hover:bg-[#0a7a0f] text-white" disabled={saving === sectionKey}>
              {saving === sectionKey ? 'Salvando...' : <><Save size={16} className="mr-2" />Salvar</>}
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );

  const hero = sections.hero || {};
  const heroMeta = hero.metadata || {};
  const fp = sections.featured_products || {};
  const diff = sections.differentials || {};
  const diffItems = diff.metadata?.items || [];
  const cta = sections.cta || {};
  const about = sections.about_company || {};
  const aboutMeta = about.metadata || {};
  const aboutStats = aboutMeta.stats || [];
  const aboutValues = aboutMeta.values || [];
  const footer = sections.footer || {};
  const footerMeta = footer.metadata || {};

  return (
    <AdminLayout>
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Editor do Site</h1>
          <p className="text-gray-600">Edite todos os textos, imagens e conteúdos do site</p>
        </div>

        <div className="space-y-4">
          {/* HERO */}
          <SectionWrapper sectionKey="hero" title="Capa Principal (Hero)">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Título</label><Input value={hero.title || ''} onChange={e => updateField('hero', 'title', e.target.value)} placeholder="Título principal" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Subtítulo</label><Input value={hero.subtitle || ''} onChange={e => updateField('hero', 'subtitle', e.target.value)} placeholder="Subtítulo" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label><textarea value={hero.content || ''} onChange={e => updateField('hero', 'content', e.target.value)} className="w-full border rounded-md px-3 py-2 text-sm min-h-[80px]" placeholder="Texto descritivo" /></div>
            <ImageField label="Imagem de Fundo" value={heroMeta.backgroundImage} onChange={v => updateMeta('hero', 'backgroundImage', v)} />
          </SectionWrapper>

          {/* FEATURED PRODUCTS */}
          <SectionWrapper sectionKey="featured_products" title="Seção Produtos Destacados">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Título da Seção</label><Input value={fp.title || ''} onChange={e => updateField('featured_products', 'title', e.target.value)} placeholder="Ex: Componentes Hidráulicos de Alta Performance" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Subtítulo</label><Input value={fp.subtitle || ''} onChange={e => updateField('featured_products', 'subtitle', e.target.value)} placeholder="Descrição da seção" /></div>
          </SectionWrapper>

          {/* DIFFERENTIALS */}
          <SectionWrapper sectionKey="differentials" title="Diferenciais">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Título da Seção</label><Input value={diff.title || ''} onChange={e => updateField('differentials', 'title', e.target.value)} /></div>
            {diffItems.map((item, i) => (
              <Card key={i} className="bg-gray-50">
                <CardContent className="pt-4 space-y-2">
                  <div className="flex justify-between items-center"><span className="text-sm font-medium">Diferencial #{i + 1}</span>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => {
                      const items = [...diffItems]; items.splice(i, 1);
                      updateMeta('differentials', 'items', items);
                    }}><Trash2 size={14} /></Button>
                  </div>
                  <Input value={item.title} onChange={e => { const items = [...diffItems]; items[i] = { ...items[i], title: e.target.value }; updateMeta('differentials', 'items', items); }} placeholder="Título" />
                  <Input value={item.description} onChange={e => { const items = [...diffItems]; items[i] = { ...items[i], description: e.target.value }; updateMeta('differentials', 'items', items); }} placeholder="Descrição" />
                </CardContent>
              </Card>
            ))}
            <Button variant="outline" size="sm" onClick={() => updateMeta('differentials', 'items', [...diffItems, { icon: 'Star', title: '', description: '' }])} className="border-[#055509] text-[#055509]"><Plus size={14} className="mr-1" />Adicionar</Button>
          </SectionWrapper>

          {/* CTA */}
          <SectionWrapper sectionKey="cta" title="Chamada para Ação (CTA)">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Título</label><Input value={cta.title || ''} onChange={e => updateField('cta', 'title', e.target.value)} /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Subtítulo</label><Input value={cta.subtitle || ''} onChange={e => updateField('cta', 'subtitle', e.target.value)} /></div>
            <ImageField label="Imagem de Fundo" value={cta.metadata?.imageUrl} onChange={v => updateMeta('cta', 'imageUrl', v)} />
          </SectionWrapper>

          {/* ABOUT */}
          <SectionWrapper sectionKey="about_company" title="Sobre a Empresa">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Título</label><Input value={about.title || ''} onChange={e => updateField('about_company', 'title', e.target.value)} /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Subtítulo / Slogan</label><Input value={about.subtitle || ''} onChange={e => updateField('about_company', 'subtitle', e.target.value)} /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Texto Principal</label><textarea value={about.content || ''} onChange={e => updateField('about_company', 'content', e.target.value)} className="w-full border rounded-md px-3 py-2 text-sm min-h-[100px]" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Ano de Fundação</label><Input type="number" value={aboutMeta.founded || ''} onChange={e => updateMeta('about_company', 'founded', parseInt(e.target.value) || '')} /></div>
            </div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Missão</label><textarea value={aboutMeta.mission || ''} onChange={e => updateMeta('about_company', 'mission', e.target.value)} className="w-full border rounded-md px-3 py-2 text-sm min-h-[60px]" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Visão</label><textarea value={aboutMeta.vision || ''} onChange={e => updateMeta('about_company', 'vision', e.target.value)} className="w-full border rounded-md px-3 py-2 text-sm min-h-[60px]" /></div>
            <ImageField label="Foto da Empresa" value={aboutMeta.imageUrl} onChange={v => updateMeta('about_company', 'imageUrl', v)} />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valores ({aboutValues.length})</label>
              {aboutValues.map((v, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <Input value={v} onChange={e => { const vals = [...aboutValues]; vals[i] = e.target.value; updateMeta('about_company', 'values', vals); }} />
                  <Button variant="ghost" size="sm" className="text-red-500" onClick={() => { const vals = [...aboutValues]; vals.splice(i, 1); updateMeta('about_company', 'values', vals); }}><Trash2 size={14} /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => updateMeta('about_company', 'values', [...aboutValues, ''])} className="border-[#055509] text-[#055509]"><Plus size={14} className="mr-1" />Adicionar Valor</Button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estatísticas ({aboutStats.length})</label>
              {aboutStats.map((s, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <Input value={s.value} onChange={e => { const stats = [...aboutStats]; stats[i] = { ...stats[i], value: e.target.value }; updateMeta('about_company', 'stats', stats); }} placeholder="Valor (ex: 400+)" className="w-32" />
                  <Input value={s.label} onChange={e => { const stats = [...aboutStats]; stats[i] = { ...stats[i], label: e.target.value }; updateMeta('about_company', 'stats', stats); }} placeholder="Label" className="flex-1" />
                  <Button variant="ghost" size="sm" className="text-red-500" onClick={() => { const stats = [...aboutStats]; stats.splice(i, 1); updateMeta('about_company', 'stats', stats); }}><Trash2 size={14} /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => updateMeta('about_company', 'stats', [...aboutStats, { value: '', label: '' }])} className="border-[#055509] text-[#055509]"><Plus size={14} className="mr-1" />Adicionar Estatística</Button>
            </div>
          </SectionWrapper>

          {/* FOOTER */}
          <SectionWrapper sectionKey="footer" title="Rodapé">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Descrição da Empresa</label><textarea value={footer.content || ''} onChange={e => updateField('footer', 'content', e.target.value)} className="w-full border rounded-md px-3 py-2 text-sm min-h-[60px]" placeholder="Texto do rodapé" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Endereço</label><Input value={footerMeta.address || ''} onChange={e => updateMeta('footer', 'address', e.target.value)} placeholder="Rua, número, cidade" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Email</label><Input value={footerMeta.email || ''} onChange={e => updateMeta('footer', 'email', e.target.value)} placeholder="contato@empresa.com" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Instagram</label><Input value={footerMeta.instagram || ''} onChange={e => updateMeta('footer', 'instagram', e.target.value)} placeholder="@usuario" /></div>
            </div>
          </SectionWrapper>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminPages;
